import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';
import './MediciNew.css';

function MediciNew() {
    // Data and loading states
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [meta, setMeta] = useState(null);

    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDoctor, setSelectedDoctor] = useState('');
    const [selectedCabinet, setSelectedCabinet] = useState('');
    const [fromDate, setFromDate] = useState(() => {
        // Default to today
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [toDate, setToDate] = useState(() => {
        // Default to 30 days from now
        const future = new Date();
        future.setDate(future.getDate() + 30);
        return future.toISOString().split('T')[0];
    });
    const [sortOrder, setSortOrder] = useState('medic'); // medic, -medic, cabinet, -cabinet, data, -data, ora_start, -ora_start

    const loadSchedules = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = {
                q: searchQuery || undefined,
                id_medic: selectedDoctor || undefined,
                id_cabinet: selectedCabinet || undefined,
                from: fromDate,
                to: toDate,
                sort: sortOrder
            };

            // Remove undefined values
            Object.keys(params).forEach(key => {
                if (params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await apiService.getSpsAvailableSchedules(params);

            if (response.ok) {
                setSchedules(response.items || []);
                setMeta(response.meta || null);
            } else {
                setError('Eroare la încărcarea programelor disponibile. Vă rugăm să reîncercați.');
                setSchedules([]);
                setMeta(null);
            }
        } catch (err) {
            console.error('Error loading schedules:', err);
            setError('Eroare la încărcarea programelor disponibile. Vă rugăm să reîncercați.');
            setSchedules([]);
            setMeta(null);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, selectedDoctor, selectedCabinet, fromDate, toDate, sortOrder]);

    // Load schedules on component mount and when filters change
    useEffect(() => {
        loadSchedules();
    }, [loadSchedules]);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('ro-RO', {
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (timeStr) => {
        // Remove seconds if present
        return timeStr.substring(0, 5);
    };

    const clearFilters = () => {
        setSearchQuery('');
        setSelectedDoctor('');
        setSelectedCabinet('');
        setFromDate(() => {
            const today = new Date();
            return today.toISOString().split('T')[0];
        });
        setToDate(() => {
            const future = new Date();
            future.setDate(future.getDate() + 30);
            return future.toISOString().split('T')[0];
        });
        setSortOrder('medic');
    };

    // Note: Helper functions for potential future use with dropdown filters
    // const getUniqueDoctors = () => {
    //     const doctorMap = new Map();
    //     schedules.forEach(schedule => {
    //         if (!doctorMap.has(schedule.nume_medic)) {
    //             doctorMap.set(schedule.nume_medic, schedule.nume_medic);
    //         }
    //     });
    //     return Array.from(doctorMap.keys());
    // };

    // const getUniqueCabinets = () => {
    //     const cabinetMap = new Map();
    //     schedules.forEach(schedule => {
    //         if (!cabinetMap.has(schedule.cabinet)) {
    //             cabinetMap.set(schedule.cabinet, schedule.cabinet);
    //         }
    //     });
    //     return Array.from(cabinetMap.keys());
    // };

    return (
        <ErrorBoundary>
            <div className="mn-page">
                {/* Header Section */}
                <div className="mn-hero">
                    <h1 className="mn-hero-title">Program Medici Disponibili</h1>
                    <p className="mn-hero-subtitle">
                        Verifică disponibilitatea medicilor în perioada dorită (SPS API)
                    </p>
                </div>

                {/* Content Section */}
                <div className="mn-content">

                    {/* Error Message */}
                    {error && (
                        <div className="mn-error">
                            <strong>Eroare:</strong> {error}
                        </div>
                    )}

                    {/* Filters Section */}
                    <div className="mn-filters">
                        <div className="mn-filter-grid">
                            {/* Date Range */}
                            <div className="mn-field">
                                <label className="mn-label">
                                    De la data:
                                </label>
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="mn-input"
                                />
                            </div>

                            <div className="mn-field">
                                <label className="mn-label">
                                    Până la data:
                                </label>
                                <input
                                    type="date"
                                    value={toDate}
                                    min={fromDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="mn-input"
                                />
                            </div>

                            {/* Search */}
                            <div className="mn-field">
                                <label className="mn-label">
                                    Caută (medic/cabinet):
                                </label>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Ex: Maria, Cabinet 3..."
                                    className="mn-input"
                                />
                            </div>

                            {/* Sort */}
                            <div className="mn-field">
                                <label className="mn-label">
                                    Sortează după:
                                </label>
                                <select
                                    value={sortOrder}
                                    onChange={(e) => setSortOrder(e.target.value)}
                                    className="mn-select"
                                >
                                    <option value="medic">Medic (A-Z)</option>
                                    <option value="-medic">Medic (Z-A)</option>
                                    <option value="cabinet">Cabinet (A-Z)</option>
                                    <option value="-cabinet">Cabinet (Z-A)</option>
                                    <option value="data">Dată crescător</option>
                                    <option value="-data">Dată descrescător</option>
                                    <option value="ora_start">Oră crescător</option>
                                    <option value="-ora_start">Oră descrescător</option>
                                </select>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="mn-actions">
                            <button
                                onClick={clearFilters}
                                className="mn-reset-btn"
                            >
                                Curăță filtre
                            </button>

                            {meta && (
                                <div className="mn-meta">
                                    {meta.count > 0 ? (
                                        <>Programări disponibile: {meta.count} în intervalul {formatDate(meta.from)} - {formatDate(meta.to)}</>
                                    ) : (
                                        <>Nu s-au găsit programări disponibile</>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Loading State — skeleton cards */}
                    {loading && (
                        <div className="skeleton-grid">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="skeleton-card">
                                    <div className="sk-header">
                                        <div className="skeleton sk-avatar" />
                                        <div className="sk-lines">
                                            <div className="skeleton sk-line-lg" />
                                            <div className="skeleton sk-line-sm" />
                                        </div>
                                    </div>
                                    <div className="skeleton sk-box" />
                                    <div className="skeleton sk-line-full" />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Schedules Grid */}
                    {!loading && schedules.length > 0 && (
                        <div className="mn-grid">
                            {schedules.map((schedule, index) => (
                                <div
                                    key={`${schedule.nume_medic}-${schedule.data}-${schedule.ora_start}-${index}`}
                                    className="mn-card"
                                >
                                    {/* Header */}
                                    <div className="mn-card-header">
                                        <div className="mn-card-avatar">👨‍⚕️</div>
                                        <div className="mn-card-info">
                                            <h3 className="mn-card-name">{schedule.nume_medic}</h3>
                                            <p className="mn-card-cabinet">{schedule.cabinet}</p>
                                        </div>
                                    </div>

                                    {/* Date and Time */}
                                    <div className="mn-card-time">
                                        <div className="mn-card-date">📅 {formatDate(schedule.data)}</div>
                                        <div className="mn-card-hours">
                                            🕐 {formatTime(schedule.ora_start)} - {formatTime(schedule.ora_sfarsit)}
                                        </div>
                                    </div>

                                    {/* Status badge */}
                                    <div className="mn-badge">Disponibil</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && schedules.length === 0 && (
                        <div className="mn-empty">
                            <div className="mn-empty-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3>Nicio programare disponibilă</h3>
                            <p>Nu s-au găsit programări disponibile pentru filtrele selectate.</p>
                            <button className="mn-empty-btn" onClick={clearFilters}>
                                Arată toate
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </ErrorBoundary>
    );
}

export default MediciNew;
